import { DOCUMENT } from "@angular/common";
import { Component, Inject, OnInit, Renderer2, RendererFactory2 } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { EnvironmentConfig, LogService, SeoRouteData, SeoService } from "@rubbl-core";
import { GoogleTagManagerService } from "angular-google-tag-manager";
import { ToastrService } from "ngx-toastr";
import { filter, map, mergeMap } from "rxjs/operators";

import { PwaUpdateService } from "./core/pwa-update.service";
import { ConnectSessionService } from "./routes/calculator/connect/connect-session.service";

declare let environment: EnvironmentConfig;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
})
export class AppComponent implements OnInit {
  toastrQueryStrings = ["Reset link sent."];
  t: boolean;

  constructor(
    private logService: LogService,
    private toastrService: ToastrService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
    private gtmService: GoogleTagManagerService,
    _pwaUpdateService: PwaUpdateService,
    _connectSession: ConnectSessionService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  ngOnInit(): void {
    // Inject Google Tag Manager on initial load so GTM-managed scripts (e.g. the
    // HubSpot chat widget) load on every route, including pages like the home page
    // that never fire a GTM event. Without this, GTM is only injected lazily on the
    // first pushTag() call, so the chat widget would not appear until the visitor
    // reached a page that pushes a tag (such as search results).
    // angular-google-tag-manager rejects with `false` when gtm.js fails to load
    // (dev uses GTM-fake-one). Zone reports that as Uncaught (in promise): [object Boolean].
    Promise.resolve(this.gtmService.addGtmToDom()).catch(() => undefined);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter(route => route.outlet === "primary"),
        mergeMap(route => route.data),
      )
      .subscribe(data => {
        this.seoService.setSeo(data as SeoRouteData);
        this.setCanonicalURLIfTrackingParams();
      });

    document.addEventListener("click", e => {
      const target = e.target as HTMLElement;
      const href = target.getAttribute("href");
      if (target.tagName === "A" && (!href || href == "#")) {
        e.preventDefault();
      }
    });

    this.checkForToastr();
    this.setPersistentStructuredData();
  }

  setCanonicalURLIfTrackingParams() {
    const location = window.location;
    // Exclude canonical update if path contains "/equipment-rentals/"
    if (location.pathname.includes("/equipment-rentals/")) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    // List of tracking param patterns (supports wildcards)
    const trackingParamPatterns = [
      /^fbclid$/i,
      /^gclid$/i,
      /^gclsrc$/i,
      /^utm_.*$/i,
      /^_ga$/i,
      /^mc_cid$/i,
      /^mc_eid$/i,
      /^_bta_tid$/i,
      /^_bta_c$/i,
      /^trk_.*$/i,
      /^gdfms$/i,
      /^gdftrk$/i,
      /^gdffi$/i,
      /^_ke$/i,
      /^sb_referer_host$/i,
      /^mkwid$/i,
      /^pcrid$/i,
      /^ef_id$/i,
      /^s_kwcid$/i,
      /^msclkid$/i,
      /^dm_i$/i,
      /^epik$/i,
      /^pk_.*$/i,
      /^piwik_.*$/i,
      /^mtm_.*$/i,
      /^matomo_.*$/i,
      /^hsa_.*$/i,
      /^_branch_match_id$/i,
      /^mkevt$/i,
      /^mkcid$/i,
      /^mkrid$/i,
      /^campid$/i,
      /^toolid$/i,
      /^customid$/i,
      /^igshid$/i,
      /^si$/i,
      /^sms_.*$/i,
    ];

    // Remove tracking params from current query string
    const canonicalParams = new URLSearchParams();
    searchParams.forEach((value, key) => {
      const isTracking = trackingParamPatterns.some(pattern => pattern.test(key)) || key.includes("_");
      if (!isTracking) {
        canonicalParams.append(key, value);
      }
    });

    // Build canonical URL with only non-tracking params (if any)
    let canonicalUrl = location.origin + location.pathname;
    const query = canonicalParams.toString();
    if (query) canonicalUrl += `?${query}`;
    if (location.hash) canonicalUrl += location.hash; // preserve hash/fragment

    let link: HTMLLinkElement = this.document.querySelector("link[rel='canonical']");
    if (link) {
      this.renderer.setAttribute(link, "href", canonicalUrl);
    } else {
      link = this.renderer.createElement("link");
      this.renderer.setAttribute(link, "rel", "canonical");
      this.renderer.setAttribute(link, "href", canonicalUrl);
      this.renderer.appendChild(this.document.head, link);
    }
  }

  checkForToastr(): void {
    // check if there is an toastr message to show
    const urlParams = new URLSearchParams(window.location.search);
    if (this.toastrQueryStrings.includes(urlParams.get("toastrMessage"))) {
      setTimeout(
        () =>
          this.toastrService.success(urlParams.get("toastrMessage"), null, {
            positionClass: "toast-top-center",
          }),
        800,
      );
      window.history.replaceState({}, window.document.title, window.location.origin);
    }
  }

  private setPersistentStructuredData(): void {
    const origin = this.document?.location?.origin ?? "https://www.rubbl.com";
    const organizationData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${origin}/#organization`,
      name: "Rubbl",
      url: origin,
      logo: "https://company.rubbl.com/pubweb/marketplace/images/logos/og-rubbl-dark.jpg",
      sameAs: [
        "https://www.facebook.com/rubblrentals",
        "https://www.instagram.com/rubblrent/",
        "https://www.linkedin.com/company/rubbl",
        "https://www.youtube.com/channel/UCATIyKNqrItKQTLWZ9iN_OA",
      ],
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer service",
          telephone: "+1-866-455-8531",
          email: "support@rubbl.com",
          areaServed: "US",
          availableLanguage: ["English"],
        },
        {
          "@type": "ContactPoint",
          contactType: "sales",
          telephone: "+1-866-455-8531",
          email: "sales@rubbl.com",
          areaServed: "US",
          availableLanguage: ["English"],
        },
      ],
      address: {
        "@type": "PostalAddress",
        streetAddress: "1012 2nd St #100",
        addressLocality: "Encinitas",
        addressRegion: "CA",
        postalCode: "92024",
        addressCountry: "US",
      },
    };

    const websiteData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${origin}/#website`,
      url: origin,
      name: "Rubbl",
      publisher: {
        "@id": `${origin}/#organization`,
      },
      potentialAction: [
        {
          "@type": "SearchAction",
          target: `${origin}/equipment-rentals?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      ],
    };

    this.seoService.upsertJsonLd(JSON.stringify(organizationData), "organization");
    this.seoService.upsertJsonLd(JSON.stringify(websiteData), "website");
  }
}
